import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "pb_access_v1";

const AccessCtx = createContext(null);

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (err) {
    console.warn("[access] Failed to read access state, resetting.", err);
    return null;
  }
}

function write(data) {
  try {
    if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn("[access] Failed to persist access state.", err);
  }
}

export function AccessProvider({ children }) {
  const [access, setAccess] = useState(read);

  // re-sync on storage events from another tab
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setAccess(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const grant = useCallback((payload) => {
    if (!payload) return;
    const next = {
      email: payload.email || null,
      entitlements: payload.entitlements || { planner: false, generator: false },
      granted_at: new Date().toISOString(),
    };
    write(next);
    setAccess(next);
  }, []);

  const revoke = useCallback(() => {
    write(null);
    setAccess(null);
  }, []);

  const value = {
    access,
    email: access?.email || null,
    entitlements: access?.entitlements || { planner: false, generator: false },
    hasPlanner: !!access?.entitlements?.planner,
    hasGenerator: !!access?.entitlements?.generator,
    grant,
    revoke,
  };

  return <AccessCtx.Provider value={value}>{children}</AccessCtx.Provider>;
}

export function useAccess() {
  const ctx = useContext(AccessCtx);
  if (!ctx) throw new Error("useAccess must be used within AccessProvider");
  return ctx;
}
